import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { 
  Menu, X, Linkedin, Mail, ArrowUpRight, Globe, 
  Briefcase, Award, CheckCircle, TrendingUp, ArrowRight, 
  Users, Server, Shield, Database, Phone, MapPin
} from 'lucide-react';

// --- REUSABLE ANIMATED LOGO COMPONENT ---
const AnimatedLogo = ({ color, width = "200px" }) => {
  const [fill, setFill] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setFill(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div style={{ width: width, display: 'flex', alignItems: 'center' }}>
      <svg viewBox="0 0 1000 300" style={{ width: '100%', height: 'auto' }} aria-label="Varun Kumar Singh Logo">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
          .logo-path {
            font-family: 'Great Vibes', cursive;
            font-size: 130px;
            stroke-width: 3px;
            stroke-dasharray: 1200;
            stroke-dashoffset: 1200;
            animation: drawLogo 2s ease-out forwards;
          }
          .logo-path.filled { transition: fill 0.5s ease; }
          @keyframes drawLogo { to { stroke-dashoffset: 0; } }
        `}</style>
        <text 
          x="50%" 
          y="50%" 
          textAnchor="middle" 
          dominantBaseline="middle" 
          className={`logo-path ${fill ? 'filled' : ''}`}
          style={{ stroke: color, fill: fill ? color : 'transparent' }}
        >
          Varun Kumar Singh
        </text>
      </svg>
    </div>
  );
};

// --- 3D BACKGROUND COMPONENT ---
const ThreeDBackground = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let width, height;
    let particles = [];

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    const initParticles = () => {
      particles = [];
      const count = width > 768 ? 80 : 40; 
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * width, y: Math.random() * height, z: Math.random() * 2,
          vx: (Math.random() - 0.5) * 0.4, vy: (Math.random() - 0.5) * 0.4, radius: Math.random() * 2 + 1,
        });
      }
    };

    const draw = () => {
      ctx.clearRect(0, 0, width, height);
      particles.forEach((p, i) => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
        const opacity = (p.z / 2) * 0.5 + 0.1;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius * (p.z + 0.5), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(21, 101, 192, ${opacity})`;
        ctx.fill();
        for (let j = i + 1; j < particles.length; j++) {
          const p2 = particles[j];
          const dx = p.x - p2.x; const dy = p.y - p2.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 150) {
            ctx.beginPath();
            ctx.strokeStyle = `rgba(46, 125, 50, ${0.15 - dist / 1500})`; 
            ctx.lineWidth = 0.5;
            ctx.moveTo(p.x, p.y); ctx.lineTo(p2.x, p2.y); ctx.stroke();
          }
        }
      });
      requestAnimationFrame(draw);
    };

    window.addEventListener('resize', resize);
    resize();
    draw();
    return () => window.removeEventListener('resize', resize);
  }, []);

  return <canvas ref={canvasRef} style={{ position: 'fixed', top: 0, left: 0, zIndex: 0, pointerEvents: 'none', opacity: 0.6 }} />;
};

// --- CONTACT FORM FIELDS ---
const ContactFormFields = ({ formData, handleChange, handleSubmit, status }) => (
  <form onSubmit={handleSubmit}>
    <div className="form-group">
        <label className="form-label" htmlFor="name">Full Name</label>
        <input id="name" type="text" name="name" className="form-input" placeholder="John Doe" value={formData.name} onChange={handleChange} required />
    </div>
    <div className="form-group">
        <label className="form-label" htmlFor="email">Email Address</label>
        <input id="email" type="email" name="email" className="form-input" placeholder="john@company.com" value={formData.email} onChange={handleChange} required />
    </div>
    <div className="form-group">
        <label className="form-label" htmlFor="subject">Subject</label>
        <input id="subject" type="text" name="subject" className="form-input" placeholder="Project Inquiry" value={formData.subject} onChange={handleChange} required />
    </div>
    <div className="form-group">
        <label className="form-label" htmlFor="message">Message</label>
        <textarea id="message" name="message" className="form-input" placeholder="Tell me about your project needs..." value={formData.message} onChange={handleChange} required></textarea>
    </div>
    <button className="submit-btn" disabled={status === 'sending'} aria-label="Send Message">
      {status === 'sending' ? 'Sending...' : 'Send Message'} 
      {status !== 'sending' && <ArrowRight size={18} style={{marginLeft: '10px'}} />}
    </button>
    {status === 'success' && <p style={{marginTop: '1rem', color: 'green', fontWeight: 'bold'}} role="alert">Message sent successfully!</p>}
    {status === 'error' && <p style={{marginTop: '1rem', color: 'red', fontWeight: 'bold'}} role="alert">Failed to send message. Please try again.</p>}
  </form>
);

// --- MAIN APP COMPONENT ---
const App = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState(null);

  useEffect(() => {
    const handleScroll = () => { setScrolled(window.scrollY > 50); };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const openModal = () => { setStatus(null); setIsModalOpen(true); setIsMenuOpen(false); };
  const closeModal = () => setIsModalOpen(false);
  const handleChange = (e) => { setFormData({ ...formData, [e.target.name]: e.target.value }); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const response = await fetch('/api/send-mail', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (response.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
        if (isModalOpen) setTimeout(closeModal, 2000);
      } else {
        setStatus('error');
      }
    } catch (error) {
      console.error(error);
      setStatus('error');
    }
  };

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      setIsMenuOpen(false);
    }
  };

  // --- Data ---
  const services = [
    { title: "Digital Transformation", desc: "Reimagining business for the digital age.", icon: <TrendingUp size={32} /> },
    { title: "Cloud Architecture", desc: "Scalable, secure infrastructure solutions.", icon: <Globe size={32} /> },
    { title: "Enterprise ERP", desc: "Seamless integration of 1C, Sage, & SAP.", icon: <Briefcase size={32} /> },
    { title: "Cybersecurity", desc: "Fortifying assets with next-gen security.", icon: <CheckCircle size={32} /> },
  ];

  const helpItems = [
    { title: "Scale Your IT Infrastructure", desc: "From local setups to global cloud architectures, I help enterprises build resilient systems that grow with them.", icon: <Server size={28} /> },
    { title: "Optimize Business Operations", desc: "Implementing world-class ERPs like SAP, Sage, and Zoho to streamline your procurement, accounts, and HR.", icon: <Database size={28} /> },
    { title: "Secure Your Digital Assets", desc: "Comprehensive cybersecurity strategies using Fortinet and SonicWall to protect against modern threats.", icon: <Shield size={28} /> },
    { title: "Expand into GCC Markets", desc: "Leveraging my partner network across UAE, KSA, and Oman to help your business establish a strong regional foothold.", icon: <Globe size={28} /> }
  ];

    const team = [
    { name: "Varun Kumar Singh", role: "Managing Director", img: "varun1.jpg" },
    { name: "Vaibhav Salunkhe", role: "IT Manager", img: "vaibhav.jpg" },
    { name: "Abhinav Singh", role: "Procurement Manager", img: "abhinav.jpg" },
    { name: "Rahul Vijayan", role: "Accounts Manager", img: "rahul.jpg" },
    { name: "Aijaz Ashraf", role: "Finance Manager", img: "azaz.jpg" },
    { name: "Roshanjamir Basha", role: "Sales Manager", img: "roshan.jpg" },
    { name: "Sangeeta Singh", role: "Digital Marketing Mgr", img: "sangeeta.jpg" },
    { name: "Mayank Bhardwaj", role: "IT Manager", img: "mayank.jpg" },
    { name: "Ashish Singh", role: "Sr. Software Consultant", img: "ashish1.jpg" },
    { name: "Nisar Ahmed", role: "ERP Consultant", img: "nisar.jpg" },
    { name: "Shivam Tiwari", role: "Software Consultant", img: "shivam.jpg" },
    { name: "Gaurav Yadav", role: "IT Consultant", img: "gaurav.jpg" },
  ];

  const gallery = [
    { title: "GITEX Global", cat: "Dubai", img: "https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=800&q=80" },
    { title: "Saudi Vision", cat: "Riyadh", img: "https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=800&q=80" },
    { title: "MWC Event", cat: "Barcelona", img: "mwc.jpg" },
  ];

  // --- Duplicate Gallery 3 times for seamless loop ---
  const loopingGallery = [...gallery, ...gallery, ...gallery];

  // --- SEO SCHEMA ---
  const structuredData = {
    "@context": "https://schema.org", "@type": "Person", "name": "Varun Kumar Singh", "jobTitle": "Managing Director & Founder",
    "worksFor": { "@type": "Organization", "name": "Infotree Computers LLC" },
    "url": "https://varunkumarsingh.com", 
    "sameAs": ["https://www.linkedin.com/in/varunkumar-singh/"],
    "description": "Varun Kumar Singh is a dynamic Managing Director leading IT services and Digital Transformation."
  };

  return (
    <div className="main-wrapper">
      <Helmet>
        <title>Varun Kumar Singh | MD & Founder of Infotree Computers LLC</title>
        <meta name="description" content="Varun Kumar Singh, Managing Director of Infotree Computers LLC, specializes in Digital Transformation, Cloud Architecture, and Enterprise ERP solutions." />
        <link rel="canonical" href="https://varunkumarsingh.com" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <ThreeDBackground />
      
      {isModalOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-content animate-in">
            <button className="modal-close" onClick={closeModal}><X size={24} /></button>
            <h3 className="section-title" style={{fontSize: '2rem', marginTop: 0}}>Let's Connect</h3>
            <p style={{marginBottom: '1.5rem', color: 'var(--text-gray)'}}>Fill out the form below and I'll get back to you shortly.</p>
            <div className="contact-form">
               <ContactFormFields formData={formData} handleChange={handleChange} handleSubmit={handleSubmit} status={status} />
            </div>
          </div>
        </div>
      )}

      <div className={`mobile-menu ${isMenuOpen ? 'open' : ''}`}>
        <nav className="mobile-nav-links">
          <a onClick={() => scrollToSection('about')}>About</a>
          <a onClick={() => scrollToSection('help')}>Services</a>
          <a onClick={() => scrollToSection('expertise')}>Expertise</a>
          <a onClick={() => scrollToSection('team')}>Team</a>
          <button className="btn-solid" onClick={openModal}>Let's Talk</button>
        </nav>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;700&family=Oswald:wght@400;500;700&display=swap');
        :root { --primary: #1565C0; --secondary: #2E7D32; --bg: #ffffff; --surface: #F4F9F4; --border: #e2e8f0; --text: #1A202C; --text-gray: #4A5568; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Manrope', sans-serif; background-color: var(--bg); color: var(--text); overflow-x: hidden; }
        .animate-in { animation: fadeIn 0.5s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        /* ... keeping standard styles ... */
        .modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.7); z-index: 9999; display: flex; justify-content: center; align-items: center; padding: 1rem; backdrop-filter: blur(5px); }
        .modal-content { background: white; padding: 2.5rem; border-radius: 8px; width: 100%; max-width: 500px; position: relative; box-shadow: 0 20px 50px rgba(0,0,0,0.2); }
        .modal-close { position: absolute; top: 15px; right: 15px; background: none; border: none; cursor: pointer; color: var(--text-gray); }
        h1, h2, h3, h4, .brand-font { font-family: 'Oswald', sans-serif; text-transform: uppercase; }
        .huge-text { font-size: 13vw; line-height: 0.85; color: var(--primary); font-weight: 700; letter-spacing: -2px; white-space: nowrap; }
        .section-title { font-size: 3rem; margin-bottom: 2rem; color: var(--primary); position: relative; display: inline-block; }
        .container { max-width: 1400px; margin: 0 auto; padding: 0 2rem; position: relative; z-index: 2; }
        .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 4rem; }
        .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 2rem; }
        .grid-4 { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1.5rem; }
        .top-bar { background-color: var(--secondary); color: white; padding: 0 3rem; height: 40px; display: flex; justify-content: space-between; align-items: center; font-size: 0.85rem; z-index: 1001; position: fixed; top: 0; left: 0; width: 100%; border-bottom: 1px solid rgba(255,255,255,0.1); }
        .top-contact { display: flex; gap: 2rem; }
        .top-contact-item { display: flex; align-items: center; gap: 0.5rem; opacity: 0.9; }
        .top-socials { display: flex; gap: 1rem; }
        .social-icon { cursor: pointer; transition: 0.2s; opacity: 0.8; }
        .navbar { position: fixed; top: 40px; left: 0; width: 100%; padding: 1.2rem 3rem; display: flex; justify-content: space-between; align-items: center; z-index: 1000; background: rgba(255,255,255,0.95); backdrop-filter: blur(12px); border-bottom: 1px solid transparent; transition: all 0.3s; }
        .navbar.scrolled { padding: 0.8rem 3rem; border-bottom: 1px solid var(--border); box-shadow: 0 4px 20px rgba(0,0,0,0.03); }
        .logo { display: flex; align-items: center; }
        .nav-links { display: flex; gap: 2rem; align-items: center; }
        .nav-link { cursor: pointer; font-weight: 500; position: relative; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; color: var(--text); }
        .btn-outline { border: 1px solid var(--primary); color: var(--primary); padding: 0.5rem 1.5rem; border-radius: 50px; font-weight: 600; transition: 0.3s; cursor: pointer; background: transparent; }
        .btn-outline:hover { background: var(--primary); color: white; }
        .btn-solid { background: var(--primary); color: white; padding: 0.8rem 2rem; border-radius: 50px; font-weight: 600; transition: 0.3s; cursor: pointer; border: none; display: inline-flex; align-items: center; gap: 10px; box-shadow: 0 10px 20px rgba(21, 101, 192, 0.2); }
        .btn-solid:hover { background: var(--secondary); transform: translateY(-3px); }
        .menu-btn { display: none; cursor: pointer; color: var(--primary); z-index: 1002; }
        .mobile-menu { position: fixed; top: 0; left: 0; width: 100%; height: 100vh; background: white; z-index: 999; display: flex; flex-direction: column; justify-content: center; align-items: center; transform: translateX(100%); transition: transform 0.4s ease-in-out; }
        .mobile-menu.open { transform: translateX(0); }
        .mobile-nav-links { display: flex; flex-direction: column; gap: 2rem; text-align: center; }
        .mobile-nav-links a { font-size: 1.5rem; font-family: 'Oswald', sans-serif; color: var(--text); text-transform: uppercase; cursor: pointer; }
        .hero { padding-top: 200px; padding-bottom: 100px; position: relative; background: transparent; }
        .hero-top-row { display: flex; justify-content: space-between; align-items: flex-end; margin-bottom: 20px; }
        .hero-role { font-size: 1.2rem; font-weight: 600; color: var(--secondary); display: flex; align-items: center; gap: 10px; }
        .hero-role::before { content: ''; width: 40px; height: 2px; background: var(--secondary); display: block; }
        .hero-layout { display: grid; grid-template-columns: 1.5fr 1fr; gap: 4rem; align-items: center; }
        .hero-desc { font-size: 1.5rem; max-width: 600px; margin-top: 2rem; line-height: 1.4; color: var(--text-gray); }
        .hero-img-frame { position: relative; height: 500px; width: 100%; background: var(--surface); border-radius: 2px; overflow: hidden; box-shadow: 0 20px 40px rgba(21, 101, 192, 0.1); }
        .hero-img { width: 100%; height: 100%; object-fit: cover; object-position: top; filter: grayscale(100%); transition: 0.5s; }
        .hero-img:hover { filter: grayscale(0%); transform: scale(1.05); }
        .marquee-wrap { background: var(--primary); color: white; padding: 1.5rem 0; overflow: hidden; white-space: nowrap; transform: rotate(-1deg); margin: 2rem -20px; border-top: 5px solid var(--secondary); position: relative; z-index: 5; }
        .marquee-content { display: inline-block; animation: scroll 20s linear infinite; }
        .marquee-item { font-family: 'Oswald', sans-serif; font-size: 2rem; margin: 0 2rem; text-transform: uppercase; }
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .help-section { padding: 6rem 0; position: relative; z-index: 2; }
        .help-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 2rem; margin-top: 3rem; }
        .help-card { border: 1px solid var(--border); padding: 2.5rem; border-radius: 8px; transition: 0.3s; background: rgba(255, 255, 255, 0.9); backdrop-filter: blur(5px); }
        .help-card:hover { border-color: var(--primary); transform: translateY(-5px); box-shadow: 0 10px 30px rgba(0,0,0,0.05); }
        .help-icon { color: var(--secondary); margin-bottom: 1.5rem; }
        .help-card h3 { font-size: 1.5rem; color: var(--primary); margin-bottom: 1rem; }
        .help-card p { color: var(--text-gray); line-height: 1.6; }
        .bio-section { background: var(--surface); padding: 8rem 0; position: relative; z-index: 2; }
        .bio-content h3 { font-size: 1.5rem; color: var(--primary); margin-top: 2rem; margin-bottom: 1rem; font-family: 'Oswald', sans-serif; }
        .bio-content p { margin-bottom: 1.5rem; font-size: 1.1rem; line-height: 1.8; color: var(--text); }
        .bio-highlight { border-left: 4px solid var(--secondary); padding-left: 2rem; margin: 2.5rem 0; font-style: italic; font-size: 1.3rem; color: var(--primary); font-family: 'Oswald', sans-serif; background: white; padding: 2rem; border-radius: 0 8px 8px 0; box-shadow: 0 5px 15px rgba(0,0,0,0.03); }
        .bento-section { padding: 8rem 0; background: white; position: relative; z-index: 2; }
        .bento-grid { display: grid; grid-template-columns: repeat(4, 1fr); grid-template-rows: repeat(2, 300px); gap: 1.5rem; }
        .bento-item { background: var(--surface); padding: 2.5rem; border-radius: 1rem; display: flex; flex-direction: column; justify-content: space-between; transition: 0.3s; border: 1px solid transparent; cursor: pointer; }
        .bento-item:hover { transform: translateY(-5px); box-shadow: 0 20px 40px -10px rgba(0,0,0,0.1); border-color: var(--border); }
        .bento-item.large { grid-column: span 2; background: linear-gradient(135deg, var(--primary), #1E88E5); color: white; }
        .bento-item.large .bento-icon { color: white; opacity: 0.9; }
        .bento-item.large p { color: rgba(255,255,255,0.8); }
        .bento-icon { margin-bottom: 1rem; color: var(--primary); }
        .bento-title { font-size: 1.5rem; margin-bottom: 0.5rem; font-weight: 700; }
        .team-section { padding: 6rem 0; background: var(--surface); position: relative; z-index: 2; }
        .team-card { background: white; padding: 1rem; border-radius: 8px; transition: 0.3s; box-shadow: 0 2px 10px rgba(0,0,0,0.02); }
        .team-card:hover { transform: translateY(-5px); box-shadow: 0 10px 20px rgba(0,0,0,0.05); }
        .team-img-box { width: 100%; aspect-ratio: 1 / 1; background: #eee; margin-bottom: 1rem; overflow: hidden; border-radius: 4px; }
        .team-img { width: 100%; height: 100%; object-fit: cover; object-position: top center; transition: 0.5s; }
        .team-card:hover .team-img { transform: scale(1.1); }
        .team-name { font-size: 1.2rem; font-weight: 700; color: var(--primary); margin-bottom: 0.2rem; }
        .team-role { color: var(--secondary); font-weight: 600; font-size: 0.8rem; text-transform: uppercase; }
        
        /* --- AUTO-SCROLL GALLERY (UNIVERSAL) --- */
        .gallery-strip { 
            overflow: hidden; 
            width: 100%; 
            position: relative;
        }
        
        /* The Moving Track */
        .gallery-track {
            display: flex;
            gap: 2rem;
            width: max-content;
            animation: galleryScroll 35s linear infinite; /* Adjusted speed */
        }
        
        /* Pause on hover */
        .gallery-track:hover {
            animation-play-state: paused;
        }
        
        /* The Cards */
        .gallery-card {
            min-width: 400px;
            height: 250px; /* Reduced height for 16:9 look */
            position: relative;
            border-radius: 1rem;
            overflow: hidden;
            flex-shrink: 0;
        }
        
        .gallery-card img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            object-position: center; /* Fixes cropping by centering image */
        }
        
        .gallery-ovl { 
            position: absolute; bottom: 0; left: 0; width: 100%; 
            padding: 2rem; 
            background: linear-gradient(transparent, rgba(21, 101, 192, 0.9)); 
            color: white; 
        }
        
        /* Keyframes: Move from 0 to -33.33% (one full set of the 3 sets) */
        @keyframes galleryScroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.333%); } 
        }

        /* Mobile adjustments for gallery */
        @media (max-width: 900px) {
            .gallery-card {
                min-width: 300px;
                height: 200px;
            }
            .gallery-track {
                gap: 1rem;
                animation-duration: 20s; /* Faster on mobile */
            }
        }

        .contact-section { padding: 8rem 0; background: white; position: relative; z-index: 2; }
        .contact-grid { display: grid; grid-template-columns: 1fr 1.5fr; gap: 4rem; background: white; padding: 4rem; border-radius: 1rem; box-shadow: 0 20px 60px -10px rgba(0,0,0,0.05); border: 1px solid var(--border); }
        .contact-info-block { margin-bottom: 2rem; }
        .contact-label { font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1px; color: var(--secondary); margin-bottom: 0.5rem; font-weight: 700; display: block; }
        .contact-value { font-size: 1.2rem; color: var(--primary); line-height: 1.6; }
        .contact-form .form-group { margin-bottom: 1.5rem; }
        .form-label { display: block; margin-bottom: 0.5rem; font-weight: 600; color: var(--text); }
        .form-input { width: 100%; padding: 1rem; border: 1px solid var(--border); border-radius: 4px; font-family: 'Manrope', sans-serif; background: var(--surface); transition: 0.3s; }
        .form-input:focus { outline: none; border-color: var(--primary); background: white; }
        textarea.form-input { min-height: 150px; resize: vertical; }
        .submit-btn { background: var(--primary); color: white; border: none; padding: 1rem 3rem; font-size: 1rem; font-weight: 600; border-radius: 4px; cursor: pointer; transition: 0.3s; width: 100%; }
        .submit-btn:hover { background: var(--secondary); }
        .submit-btn:disabled { background: #ccc; cursor: not-allowed; }
        .footer-main { background-color: #0F172A; color: white; padding: 5rem 0 0; position: relative; z-index: 2; }
        .footer-grid { display: grid; grid-template-columns: 1.5fr 1fr 1fr 1.2fr; gap: 3rem; margin-bottom: 4rem; }
        .footer-brand h4 { font-size: 1.8rem; margin-bottom: 1.5rem; color: white; }
        .footer-brand p { color: #94a3b8; line-height: 1.7; max-width: 300px; }
        .footer-col h5 { color: white; margin-bottom: 1.5rem; font-size: 1.1rem; text-transform: uppercase; letter-spacing: 1px; }
        .footer-link { display: block; color: #94a3b8; margin-bottom: 0.8rem; text-decoration: none; transition: 0.3s; }
        .footer-link:hover { color: var(--secondary); padding-left: 5px; }
        .footer-contact-item { display: flex; gap: 1rem; margin-bottom: 1.5rem; color: #94a3b8; }
        .footer-bottom { border-top: 1px solid rgba(255,255,255,0.1); padding: 2rem 0; text-align: center; color: #64748b; font-size: 0.9rem; }
        @media (max-width: 900px) {
            .huge-text { font-size: 18vw; }
            .hero-layout { grid-template-columns: 1fr; gap: 2rem; }
            .grid-2 { grid-template-columns: 1fr; gap: 2rem; }
            .bento-grid { grid-template-columns: 1fr; grid-template-rows: auto; }
            .bento-item.large { grid-column: span 1; }
            .nav-links { display: none; }
            .menu-btn { display: block; }
            .navbar { padding: 1rem 1.5rem; justify-content: space-between; }
            .section-title { font-size: 2.2rem; }
            .hero-desc { font-size: 1.1rem; }
            .btn-solid { width: 100%; justify-content: center; }
            .grid-4, .grid-3 { grid-template-columns: 1fr; }
            .hero-img-frame { height: 300px; }
            .marquee-wrap { transform: rotate(0deg); }
            .top-bar { padding: 0 1.5rem; font-size: 0.75rem; }
            .top-socials { display: none; }
            .contact-grid { grid-template-columns: 1fr; padding: 1.5rem; }
            .footer-grid { grid-template-columns: 1fr; text-align: center; }
            .footer-contact-item { justify-content: center; }
            .footer-brand p { margin: 0 auto 1.5rem auto; }
        }
      `}</style>

      {/* Top Bar */}
      <header className="top-bar">
        <div className="top-contact">
            <a href="tel:+971564427403" className="top-contact-item" style={{color: 'white', textDecoration: 'none'}}>
                <Phone size={14} /> <span>+971 56 442 7403</span>
            </a>
            <a href="mailto:info@infotreeit.com" className="top-contact-item" style={{color: 'white', textDecoration: 'none'}}>
                <Mail size={14} /> <span>varun@infotreeit.com</span>
            </a>
        </div>
        <div className="top-socials">
            <a href="https://www.linkedin.com/in/varunkumar-singh/" target="_blank" rel="noreferrer" className="social-icon" aria-label="LinkedIn Profile">
                <Linkedin size={16} color="white" />
            </a>
        </div>
      </header>

      {/* Navigation */}
      <nav className={`navbar ${scrolled ? 'scrolled' : ''}`} role="navigation">
        <div className="logo">
           <AnimatedLogo color="var(--primary)" width="200px" />
        </div>
        <div className="nav-links">
          <span className="nav-link" onClick={() => scrollToSection('about')}>About</span>
          <span className="nav-link" onClick={() => scrollToSection('help')}>Services</span>
          <span className="nav-link" onClick={() => scrollToSection('expertise')}>Expertise</span>
          <span className="nav-link" onClick={() => scrollToSection('team')}>Team</span>
          <button className="btn-outline" onClick={openModal}>Let's Talk</button>
        </div>
        <div className="menu-btn" onClick={toggleMenu} aria-label="Toggle Menu">
          {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
        </div>
      </nav>

      {/* Main Content */}
      <main>
        <header className="container hero">
          <div className="hero-top-row">
              <div className="hero-role">MD & FOUNDER</div>
              <div style={{fontFamily: 'Oswald', color: 'var(--secondary)'}}>EST. 2015</div>
          </div>
          <h1 className="huge-text">DIGITAL<br/>EMPIRES</h1>
          <div className="hero-layout">
              <div>
                  <p className="hero-desc">
                      I build bridges between complex business challenges and elegant technological solutions. 
                      Leading <strong>Infotree Computers LLC</strong> to new heights across the MENA region.
                  </p>
                  <div style={{marginTop: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap'}}>
                      <button className="btn-outline" onClick={() => scrollToSection('profile')}>Read Full Bio</button>
                      <button className="btn-solid" onClick={openModal}>
                          Connect with Varun <ArrowRight size={18} />
                      </button>
                  </div>
              </div>
              <div className="hero-img-frame">
                  <img src="varun.jpg" alt="Varun Kumar Singh - MD of Infotree Computers" className="hero-img" />
              </div>
          </div>
        </header>

        <div className="marquee-wrap">
          <div className="marquee-content">
              <span className="marquee-item">Digital Transformation</span> • 
              <span className="marquee-item">Cloud Architecture</span> • 
              <span className="marquee-item">Enterprise Security</span> • 
              <span className="marquee-item">Global Expansion</span> • 
              <span className="marquee-item">Strategic M&A</span> • 
              <span className="marquee-item">Digital Transformation</span> • 
              <span className="marquee-item">Cloud Architecture</span> • 
          </div>
        </div>

        <section id="help" className="container help-section">
          <h2 className="section-title">HOW CAN I HELP YOU?</h2>
          <div className="help-grid">
              {helpItems.map((item, index) => (
                  <article className="help-card" key={index}>
                      <div className="help-icon">{item.icon}</div>
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                  </article>
              ))}
          </div>
        </section>

        <section id="profile" className="bio-section">
          <div className="container">
              <div className="grid-2">
                  <div>
                      <h2 className="section-title">THE MAN BEHIND<br/>THE VISION</h2>
                      <img src="varun.jpg" alt="Varun Kumar Singh Leadership Profile" style={{width: '100%', borderRadius: '4px', marginTop: '2rem', boxShadow: '0 20px 40px rgba(0,0,0,0.1)'}} />
                  </div>
                  <article className="bio-content">
                      <h3>The Journey</h3>
                      <p>
                          Varun Kumar Singh is a dynamic and growth-driven Managing Director with over 12 years of experience leading IT services, CRM/ERP product development, and managed services. Based in Dubai, he has successfully scaled IT companies, established massive reseller ecosystems, and driven multi-million-dirham revenues.
                      </p>
                      <p>
                          His journey began in 2015 with the founding of <strong>InfoSeed Computers</strong>, which focused on IT consulting for SMEs. Seeing the rapid digitization needs of the region, he registered <strong>InfoTree Computers LLC</strong> in 2016, expanding into ERP, Cloud Hosting, and Cybersecurity.
                      </p>
                      <p>
                          Varun’s strategic vision led to key acquisitions, including <strong>Technoware Computers LLC</strong> in 2018 to enhance hardware capabilities, and <strong>First Net Gulf</strong> in 2022 to penetrate the Saudi Arabian market (KSA) with large-scale infrastructure projects supporting Saudi Vision 2030.
                      </p>
                      
                      <blockquote className="bio-highlight">
                          "Building Trust, Winning Hearts. With extensive experience and a strategic mindset, I am well-equipped to drive growth and success."
                      </blockquote>

                      <h3>Strategic Expertise</h3>
                      <p>
                          As Managing Director and Co-Founder of Infotree Group, Varun brings deep expertise in financial accounting, channel management, and data analysis. His skills in financial accounting allow him to oversee complex financial operations, including budgeting and forecasting, ensuring the financial health of the businesses he leads.
                      </p>
                      <p>
                          His background in data analysis enables him to leverage large datasets to make informed business decisions, presenting actionable insights to key stakeholders that improve operational efficiency and increase profitability. Additionally, his expertise in channel management has been pivotal in developing strong relationships with partners and suppliers, directly contributing to increased revenue and market share.
                      </p>
                      <p>
                          Today, he oversees a portfolio of <strong>2,300+ active clients</strong> and manages a partner network spanning 20+ countries. He is also the founder of <strong>EAZZTREE Technologies</strong>, launching AI-powered CRM solutions to modernize sales processes globally.
                      </p>

                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
                          <AnimatedLogo color="var(--primary)" width="250px" />
                      </div>
                  </article>
              </div>
          </div>
        </section>

        <section id="expertise" className="bento-section">
          <div className="container">
              <h2 className="section-title">CORE EXPERTISE</h2>
              <div className="bento-grid">
                  <div className="bento-item large">
                      <div className="bento-icon"><Globe size={40} /></div>
                      <div>
                          <h3 className="bento-title">Global Infrastructure</h3>
                          <p>Managing large-scale IT deployments across UAE, KSA, and India.</p>
                      </div>
                  </div>
                  {services.map((s, i) => (
                      <div key={i} className="bento-item">
                          <div className="bento-icon">{s.icon}</div>
                          <div>
                              <h3 className="bento-title" style={{fontSize: '1.2rem'}}>{s.title}</h3>
                              <p style={{fontSize: '0.9rem', color: '#666'}}>{s.desc}</p>
                          </div>
                      </div>
                  ))}
                  <div className="bento-item" style={{background: 'var(--secondary)', color: 'white'}}>
                      <div className="bento-icon" style={{color: 'white'}}><Award size={32} /></div>
                      <h3 className="bento-title">Certified</h3>
                      <p style={{color: 'rgba(255,255,255,0.8)'}}>Intuit Star Performer & Gold Partner.</p>
                  </div>
              </div>
          </div>
        </section>

        <section id="team" className="team-section">
          <div className="container">
              <h2 className="section-title">THE POWER TEAM</h2>
              <div className="grid-4">
                  {team.map((m, i) => (
                      <div key={i} className="team-card">
                          <div className="team-img-box">
                              <img src={m.img} className="team-img" alt={m.name} />
                          </div>
                          <div className="team-name brand-font">{m.name}</div>
                          <div className="team-role">{m.role}</div>
                      </div>
                  ))}
              </div>
          </div>
        </section>

        {/* Gallery Strip - Updated for Universal Auto Scroll */}
        <section className="container" style={{marginBottom: '5rem', marginTop: '5rem'}}>
          <h2 className="section-title">GLOBAL FOOTPRINT</h2>
          <div className="gallery-strip">
              <div className="gallery-track">
                  {loopingGallery.map((g, i) => (
                      <div key={i} className="gallery-card">
                          <img src={g.img} alt={g.title} />
                          <div className="gallery-ovl">
                              <span style={{color: '#4ade80', fontSize: '0.8rem', textTransform: 'uppercase'}}>{g.cat}</span>
                              <h3 className="brand-font" style={{fontSize: '1.5rem'}}>{g.title}</h3>
                          </div>
                      </div>
                  ))}
              </div>
          </div>
        </section>

        <section id="contact" className="contact-section">
          <div className="container">
              <h2 className="section-title">GET IN TOUCH</h2>
              <div className="contact-grid">
                  <div className="contact-details">
                      <h3 style={{fontSize: '2rem', marginBottom: '2rem', fontFamily: 'Oswald'}}>LET'S BUILD SOMETHING GREAT.</h3>
                      <p style={{marginBottom: '3rem', color: '#64748b'}}>
                          Whether you are looking for IT consultation, ERP implementation, or partnership opportunities, I'd love to hear from you.
                      </p>
                      <div className="contact-info-block">
                          <span className="contact-label">Headquarters</span>
                          <span className="contact-value">Office No. 504, 5th Floor<br/>Al-Zahra Techno Centre<br/>Khalid Bin Al Waleed Rd, Bur Dubai</span>
                      </div>
                      <div className="contact-info-block">
                          <span className="contact-label">Contact</span>
                          <span className="contact-value">+971 56 442 7403<br/>varun@infotreeit.com</span>
                      </div>
                      <div className="contact-info-block">
                          <span className="contact-label">Connect</span>
                          <div style={{display: 'flex', gap: '1rem', marginTop: '0.5rem'}}>
                              <a href="https://www.linkedin.com/in/varunkumar-singh/" target="_blank" rel="noreferrer" aria-label="Connect on LinkedIn">
                                  <Linkedin size={24} color="var(--primary)" />
                              </a>
                          </div>
                      </div>
                  </div>
                  <div className="contact-form">
                      <ContactFormFields formData={formData} handleChange={handleChange} handleSubmit={handleSubmit} status={status} />
                  </div>
              </div>
          </div>
        </section>
      </main>
      
      {/* Footer */}
      <footer className="footer-main">
        <div className="container">
            <div className="footer-grid">
                <div className="footer-brand">
                    <AnimatedLogo color="#ffffff" width="250px" />
                    <p style={{marginTop: '1rem'}}>Driving innovation, digital transformation, and global growth across the Middle East, Africa, and beyond.</p>
                </div>
                <div className="footer-col">
                    <h5>Quick Links</h5>
                    <a href="#" className="footer-link">About Me</a>
                    <a href="#" className="footer-link">My Journey</a>
                    <a href="#" className="footer-link">Portfolio</a>
                    <a href="#" className="footer-link">Contact</a>
                </div>
                <div className="footer-col">
                    <h5>Services</h5>
                    <a href="https://infotreeit.com" target="_blank" rel="noreferrer" className="footer-link">Digital Transformation</a>
                    <a href="https://infotreeit.com" target="_blank" rel="noreferrer" className="footer-link">Cloud Hosting</a>
                    <a href="https://infotreeit.com" target="_blank" rel="noreferrer" className="footer-link">ERP Solutions</a>
                    <a href="https://infotreeit.com" target="_blank" rel="noreferrer" className="footer-link">Cybersecurity</a>
                </div>
                <div className="footer-col">
                    <h5>Visit Us</h5>
                    <div className="footer-contact-item">
                        <MapPin size={20} style={{flexShrink: 0}} />
                        <span>Office 504, Al-Zahra Techno Centre, Bur Dubai, UAE</span>
                    </div>
                    <div className="footer-contact-item">
                        <Phone size={20} style={{flexShrink: 0}} />
                        <span>+971 56 442 7403</span>
                    </div>
                    <div className="footer-contact-item">
                        <Mail size={20} style={{flexShrink: 0}} />
                        <span>varun@infotreeit.com</span>
                    </div>
                </div>
            </div>
            <div className="footer-bottom">
                &copy; {new Date().getFullYear()} Varun Kumar Singh & InfoTree Computers LLC. All Rights Reserved.
            </div>
        </div>
      </footer>
    </div>
  );
};

export default App;
